import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import "./FormStyles.css";
import "./User.css";
import useAxios from "./useAxios";
import useSessionStorage from "./useSessionStorage";
import ConfirmDialog from "./ConfirmDialog";

const User = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [players, setPlayers] = useState([]);
  const [axios] = useAxios(true);
  const [tenant] = useSessionStorage("tenant");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id === "new") {
      setUser({
        name: "",
        tenant: "",
        username: "",
        email: "",
        password: "",
        userType: "user",
        profilePictureUrl: "",
        playerId: null,
      });
    } else {
      axios.get("/user/" + id, setUser);
    }
    axios.get("/tenants", setTenants);
  }, [id]);

  // Fetch players when tenant changes
  useEffect(() => {
    if (user?.tenant) {
      axios.get("/players", setPlayers);
    }
  }, [user?.tenant]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Handle PlayerId specially to convert empty string to null
    const processedValue = name === "playerId" && value === "" ? null : 
                          name === "playerId" ? parseInt(value) : value;
    setUser((prevUser) => ({ ...prevUser, [name]: processedValue }));
  };

  const onSave = () => {
    axios.post("/user/save/", user, onCancel);
  };

  const onCancel = () => {
    navigate("/configuration/users");
  };

  const handleDeleteClick = () => {
    setDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    axios.delete(`/user/${id}`, () => {
      navigate("/configuration/users");
    });
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(false);
  };

  if (user === null || tenants.length === 0) {
    return <h1>Loading</h1>;
  }

  return (
    <div className="edit-user-form">
      <h2>{id === "new" ? "Create User" : "Edit User"}</h2>
      <form className="user-form-fields">
        <div className="form-group">
          <label htmlFor="name">Name:</label>
          <input
            type="text"
            name="name"
            id="name"
            value={user.name}
            onChange={handleChange}
            required
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="tenant">Tenant:</label>
          <select
            name="tenant"
            id="tenant"
            value={user.tenant}
            onChange={handleChange}
            required
            className="form-control"
          >
            <option value="">Select a tenant</option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="username">Username:</label>
          <input
            type="text"
            name="username"
            id="username"
            value={user.username}
            onChange={handleChange}
            required
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="email">Email:</label>
          <input
            type="email"
            name="email"
            id="email"
            value={user.email}
            onChange={handleChange}
            required
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="profilePictureUrl">Profile Picture URL:</label>
          <input
            type="url"
            name="profilePictureUrl"
            id="profilePictureUrl"
            value={user.profilePictureUrl || ""}
            onChange={handleChange}
            className="form-control"
            placeholder="https://example.com/profile.jpg"
          />
        </div>
        <div className="form-group">
          <label htmlFor="playerId">Linked Player:</label>
          <select
            name="playerId"
            id="playerId"
            value={user.playerId || ""}
            onChange={handleChange}
            className="form-control"
          >
            <option value="">No player linked</option>
            {players.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name} (Handicap: {player.handicap})
              </option>
            ))}
          </select>
          {user.playerId && user.player && (
            <div style={{ marginTop: "5px", fontSize: "0.9em", color: "#666" }}>
              Currently linked to: {user.player.name} (Handicap: {user.player.handicap})
            </div>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="password">Password:</label>
          <input
            type="password"
            name="password"
            id="password"
            value={user.password}
            onChange={handleChange}
            required={id === "new"}
            className="form-control"
          />
        </div>
        <div className="form-group">
          <label htmlFor="userType">User Type:</label>
          <select
            name="userType"
            id="userType"
            value={user.userType || "user"}
            onChange={handleChange}
            required
            className="form-control"
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-buttons">
          <button type="button" onClick={onSave} className="submit-button">
            Save
          </button>
          <button type="button" onClick={onCancel} className="cancel-button">
            Cancel
          </button>
          {tenant.super && id !== "new" && (
            <button
              type="button"
              onClick={handleDeleteClick}
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                border: "1px solid #dc3545",
                padding: "8px 16px",
                borderRadius: "4px",
                cursor: "pointer",
                marginLeft: "8px",
              }}
            >
              Delete User Data
            </button>
          )}
        </div>
      </form>
      
      <ConfirmDialog
        isOpen={deleteConfirm}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        variant="danger"
        title="⚠️ GDPR Data Deletion Warning"
        message={
          user
            ? `You are about to permanently delete all data for user: ${user.name} (${user.email}). THIS ACTION CANNOT BE UNDONE.`
            : ""
        }
        details={[
          "User profile and personal information",
          "Account credentials and authentication data",
          "All associated user claims and permissions",
          "Any other user-related data in the system"
        ]}
        confirmText="Yes, Permanently Delete User Data"
        cancelText="Cancel"
      />
    </div>
  );
};

export default User;
